import { useState, useCallback } from 'react';
import { queryClient } from 'react-query';
import toast from 'react-hot-toast';
import { confirmLead, updateLead } from 'api/leads';
import { useTanstackMutation } from 'hooks';
import { Lead, LeadApiResponse } from 'models/Lead';
import { API_PATHS } from 'api';

export type LeadMutations = {
  executeLeadMutations: (
    leadId: string,
    contact: any,
    values: any,
  ) => Promise<void>;
  isUpdating: boolean;
  isConfirming: boolean;
};

const useLeadMutations = (
  lead: Lead,
  onSuccess?: () => void,
  isPersonal = false,
): LeadMutations => {
  const [updating, setUpdating] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const { mutateAsync: saveAsync } = useTanstackMutation<LeadApiResponse>({
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(
        [`${isPersonal ? 'personal-' : ''}lead`, { id: updatedLead._id }],
        updatedLead,
      );
    },
    onError: (error) => {
      console.error('Error updating lead', error);
      toast.error(`Error on updating lead.`);
    },
  });

  const { mutateAsync: confirmAsync } = useTanstackMutation<LeadApiResponse>({
    onSuccess: (confirmedLead) => {
      queryClient.setQueryData(
        [`${isPersonal ? 'personal-' : ''}lead`, { id: confirmedLead._id }],
        confirmedLead,
      );
    },
    onError: (error) => {
      console.error('Error confirming lead', error);
      toast.error(`Error on confirming lead.`);
    },
  });

  const executeLeadMutations = useCallback(
    async (leadId: string, contact: any, values: any) => {
      const { submitAction, email, subject, message, first_name, last_name } =
        values;

      if (submitAction === 'saveAndConfirm') {
        try {
          setConfirming(true);

          await saveAsync({
            path: isPersonal
              ? API_PATHS.personalLeads.getLeadById(leadId)
              : updateLead(leadId),
            method: 'patch',
            body: {
              email,
              first_outreach: message,
              first_subject: subject,
              first_name,
              last_name,
            },
          });

          await confirmAsync({
            path: isPersonal
              ? API_PATHS.personalLeads.confirmLead(leadId)
              : confirmLead(leadId),
            method: 'post',
          });
          queryClient.invalidateQueries({
            queryKey: [`${isPersonal ? 'personal-' : ''}leads`],
          });
          queryClient.invalidateQueries({
            queryKey: [`${isPersonal ? 'personal-' : ''}tabs`],
          });
          toast.success(`Successfully confirmed lead`);
          onSuccess?.();
        } catch (error) {
          console.error('Error during saveAndConfirm', error);
        } finally {
          setConfirming(false);
        }
      } else if (submitAction === 'save') {
        try {
          setUpdating(true);
          await saveAsync({
            path: isPersonal
              ? API_PATHS.personalLeads.getLeadById(leadId)
              : updateLead(leadId),
            method: 'patch',
            body: {
              email,
              first_outreach: message,
              first_subject: subject,
              first_name,
              last_name,
            },
          });
          queryClient.invalidateQueries({
            queryKey: [`${isPersonal ? 'personal-' : ''}leads`],
          });
          queryClient.invalidateQueries({
            queryKey: [`${isPersonal ? 'personal-' : ''}tabs`],
          });
          toast.success(`Successfully updated lead`);
          onSuccess?.();
        } catch (error) {
          console.error('Error during save', error);
        } finally {
          setUpdating(false);
        }
      }
    },
    [confirmAsync, isPersonal, onSuccess, saveAsync],
  );

  return {
    executeLeadMutations,
    isUpdating: updating,
    isConfirming: confirming,
  };
};

export default useLeadMutations;
